<template>
  <modal :open="internalOpen" :width="900">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.driver.payoutList.editPayoutModal.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <Multiselect
        v-model="status"
        :error="errors?.status"
        :options="payoutList"
        :label="$t('payout.status')"
        class="w-full mb-5"
        :can-deselect="false"
        :can-clear="false"
      />
      <div v-if="!['inPayment', 'payed'].includes(editPayout?.status)" class="mb-5 text-sm">
        {{ t('pages.driver.payoutList.editPayoutModal.helpText') }}
      </div>
      <text-area
        v-model="rawPayout.comment"
        :error="errors?.comment"
        :label="$t('payout.comment')"
        class="w-full mb-5"
      />
      <PayoutEntries v-model:entries="rawPayout.entries" :errors="errors" :driver="editPayout?.driver" />
      <div class="w-full text-center">
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loading"
          @click="save"
        >
          {{ $t('save') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  array, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import PayoutEntries from '@pages/modals/PayoutEntries.vue';
import type { Payout } from '@/entities/Entities';
import {computed, ref, watch} from 'vue';

interface Props {
  open: boolean;
  editPayout: Payout;
}

const emit = defineEmits(['update:open', 'update:editPayout', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const rawPayout = useVModel(props, 'editPayout', emit);

const { t } = useI18n();

const notify = useNotify();

const {
  data: payout,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    comment: string(),
    entries: array(
      object({
        subject: string(),
        amount: number().typeError(t('validation.notEmpty')),
      }),
    ),
  }),
  rawPayout,
);

const status = ref();

watch(() => props.editPayout?.status, () => {
  status.value = props.editPayout.status;
});

const fetchPayoutStatus = ref();
const fetchPayoutStatusFunc = async () => {
  fetchPayoutStatus.value = await enumOptionsProvider('/enums/PayoutStatus', (item) => ({
    value: item,
    label: t(`payoutStatus.${item}`),
  }))();
};
fetchPayoutStatusFunc();

const payoutList = computed(() => ['inPayment', 'payed'].includes(props.editPayout?.status) ? fetchPayoutStatus.value : fetchPayoutStatus.value?.map(item => ({ ...item, disabled: item.value === 'payed', tooltip: 'blaa' }) ));

function resetData() {
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec(`/payouts/${props.editPayout.id}`, {
      method: 'PUT',
      body: {
        status: status.value,
        comment: payout.value.comment,
        entries: payout.value.entries.map((entry) => ({
          ...entry,
          id: entry['@id'],
        })),
      },
    });

    payout.value.status = status.value;

    notify?.({
      variant: 'success',
      title: t('pages.driver.payoutList.editPayoutModal.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.payoutList.editPayoutModal.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped>
:deep(.multiselect-option.is-disabled) {
  background-color: var(--color-element-neutral-100);
}
</style>
